import { ApiError } from "@fm/api";
import type { CheckoutScreeningQuestion } from "@fm/api/screening-questions-checkout";
import type { ScreeningErrors } from "./screening";
import {
  extractErrorCode,
  extractErrorMessage,
  getDeliveryErrorInfo,
} from "./deliveryErrors";

export interface ParsedSoftHoldError {
  message: string;
  /** Per-question messages from a screening 422, keyed by question id. */
  fieldErrors: ScreeningErrors;
  /** The server's `code`, when it sent one (e.g. a delivery refusal). */
  code?: string;
}

const GENERIC_MESSAGE = "An error occurred during checkout initialization";

/**
 * Turns a failed `POST /soft-holds` into text for the checkout. The server's
 * own message (notice period, screening, availability) is shown as-is; a
 * screening 422 also carries `errors: { question_id => message }` so each
 * answer can be flagged next to its input. Labels are resolved from the
 * catalog the checkout already fetched (`questions`) rather than a
 * hardcoded map — an id the current catalog does not recognise (e.g. a
 * paused or deleted question) is silently dropped, same as before. A
 * delivery refusal (`code`) is worded for the renter instead.
 */
export function parseSoftHoldError(
  error: unknown,
  questions: CheckoutScreeningQuestion[] = [],
): ParsedSoftHoldError {
  const parsed = parseServerMessage(error, questions);
  const code = extractErrorCode(error);
  if (!code) return parsed;

  // A delivery refusal gets clear renter-facing wording; any other code keeps
  // the server's own message.
  const info = getDeliveryErrorInfo(code);
  return { ...parsed, message: info?.message ?? parsed.message, code };
}

function parseServerMessage(
  error: unknown,
  questions: CheckoutScreeningQuestion[],
): ParsedSoftHoldError {
  const fieldErrors: ScreeningErrors = {};
  const labels = new Map(questions.map((q) => [String(q.id), q.label]));
  const responseError = error as {
    response?: { status?: number; data?: { errors?: unknown } };
  } | null;
  const responseData = responseError?.response?.data;

  if (
    error instanceof ApiError ||
    (responseError?.response?.status === 422 && responseData)
  ) {
    const raw =
      error instanceof ApiError
        ? (error.data as { errors?: unknown } | undefined)?.errors
        : responseData?.errors;

    if (raw && typeof raw === "object") {
      for (const [key, detail] of Object.entries(raw)) {
        const label = labels.get(key);
        if (label && typeof detail === "string") {
          fieldErrors[key] = `${label} ${detail}.`;
        }
      }
    }

    return {
      message:
        error instanceof Error && error.message
          ? (extractErrorMessage(error) ?? error.message)
          : GENERIC_MESSAGE,
      fieldErrors,
    };
  }

  if (error instanceof Error && error.message) {
    return {
      message: extractErrorMessage(error) ?? error.message,
      fieldErrors,
    };
  }

  return { message: GENERIC_MESSAGE, fieldErrors };
}
